import logging from '@sstdev/lib_logging';
import lodash from 'lodash';
import propTypes from 'prop-types';
import { forwardRef, createElement as rc, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import FocusContext from '../contexts/FocusContext';
import fromTheme from '../fromTheme';
import useEnsureRef from '../hooks/useEnsureRef';
import styled from '../styled';
import BaseIcon from './Icons/BaseIcon.gl2025';
const { isEqual } = lodash;


// todo gl2025: Remove InputOld and TextAreaOld as well as conditional rendering of each.
//  Remove checks for themeName === 'gl2025' in renders

const InputOld = styled.input.attrs(props => ({
    name: props.name || 'text',
    type: props.type || 'textbox'
}))`
    font-family: ${fromTheme('font')};
    font-size: ${fromTheme('fontSize')};
    padding: ${fromTheme('textPadding')};
    border-radius: 0;
    border: none;
    color: ${({ disabled, theme }) => (disabled ? theme.disabledFontColor : theme.defaultFontColor)};
    border-width: 1px;
    border-style: solid;
    border-color: transparent;
    border-bottom-color: ${fromTheme('borderColor')};
    background-color: transparent;
    flex-grow: 1;
    margin-bottom: 1px;
    &:focus {
        outline: none;
        border-bottom-width: 2px;
        border-bottom-color: ${fromTheme('colorScheme', 'primary')};
        margin-bottom: 0;
    }
`;

const TextAreaOld = styled.textarea.attrs(props => ({
    name: props.name || 'textarea'
}))`
    font-family: ${fromTheme('font')};
    font-size: ${fromTheme('fontSize')};
    padding: ${fromTheme('textPadding')};
    border-radius: 0;
    border: none;
    color: ${({ disabled, theme }) => (disabled ? theme.disabledFontColor : theme.defaultFontColor)};
    border-width: 1px;
    border-style: solid;
    border-color: transparent;
    border-bottom-color: ${fromTheme('borderColor')};
    background-color: ${fromTheme('backgroundColor')} !important;
    width: ${({ theme }) => 'calc(100% - ' + theme.textPadding * 2 + 'px)'};
    margin-bottom: 1px;
    &:focus {
        outline: none;
        border-bottom-width: 2px;
        border-bottom-color: ${fromTheme('colorScheme', 'primary')};
        margin-bottom: 0;
    }
`;

const TextAreaGl2025 = styled.textarea`
    display: flex;
    align-items: center;
    align-self: stretch;
    padding: ${fromTheme('input', 'padding')};
    gap: ${fromTheme('input', 'gap')};

    border-radius: ${fromTheme('input', 'borderRadius')};
    border-width: ${fromTheme('input', 'borderWidth')};
    border-style: ${fromTheme('input', 'borderStyle')};
    border-color: ${({ destructive }) => destructive ?
        fromTheme('input', 'borderColorDestructive', 'default') :
        fromTheme('input', 'borderColor', 'default')};
    background: ${fromTheme('input', 'backgroundColor', 'default')};

    color: ${fromTheme('input', 'fontColor', 'default')};
    ${fromTheme('input', 'font')};

    &:hover:not([disabled]):not(:focus) {
        border-color: ${({ destructive }) => destructive ?
            fromTheme('input', 'borderColorDestructive', 'hover') :
            fromTheme('input', 'borderColor', 'hover')};
    }

    &:focus {
        outline: none;
        border-color: ${({ destructive }) => destructive ?
    fromTheme('input', 'borderColorDestructive', 'focus') :
    fromTheme('input', 'borderColor', 'focus')};
    }

    &:disabled {
        border-color: ${fromTheme('input', 'borderColor', 'disabled')};
        background: ${fromTheme('input', 'backgroundColor', 'disabled')};
        color: ${fromTheme('input', 'fontColor', 'disabled')};
    }

    ::placeholder {
        color: ${fromTheme('input', 'placeholderFontColor')};
    }
`;

const InputGl2025 = styled.input`
    display: flex;
    align-items: center;
    align-self: stretch;
    padding: ${fromTheme('input', 'padding')};
    gap: ${fromTheme('input', 'gap')};

    border-width: 0;   // no border on the input itself, just the wrapper
    border-style: ${fromTheme('input', 'borderStyle')};
    border-color: ${fromTheme('input', 'borderColor', 'default')};
    border-radius: ${fromTheme('input', 'borderRadius')};

    background: ${fromTheme('input', 'backgroundColor', 'default')};

    color: ${fromTheme('input', 'fontColor', 'default')};
    ${fromTheme('input', 'font')};

    ${({ itemLeading, destructive, theme }) =>
        itemLeading &&
        `
            border-left-width: ${fromTheme('input', 'borderWidth')({ theme })};
            border-left-style: ${fromTheme('input', 'borderStyle')({ theme })};
            border-left-color: ${ destructive ?
                fromTheme('input', 'borderColorDestructive', 'default')({ theme }) :
                fromTheme('input', 'borderColor', 'default')({ theme })};
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
        `
    };

    ${({ itemTrailing, pinnedValue, destructive, theme }) =>
        itemTrailing && !pinnedValue &&
        `
            border-right-width: ${fromTheme('input', 'borderWidth')({ theme })};
            border-right-style: ${fromTheme('input', 'borderStyle')({ theme })};
            border-right-color: ${ destructive ?
                fromTheme('input', 'borderColorDestructive', 'default')({ theme }) :
                fromTheme('input', 'borderColor', 'default')({ theme })};
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
        `
    };

    &:hover:not([disabled]):not(:focus) {
        border-color: ${({ destructive }) => destructive ?
            fromTheme('input', 'borderColorDestructive', 'hover') :
            fromTheme('input', 'borderColor', 'hover')};
    }

    &:focus {
        outline: none;
        border-color: ${({ destructive }) => destructive ?
            fromTheme('input', 'borderColorDestructive', 'focus') :
            fromTheme('input', 'borderColor', 'focus')};
    }

    &:disabled {
        border-color: ${fromTheme('input', 'borderColor', 'disabled')};
        background: ${fromTheme('input', 'backgroundColor', 'disabled')};
        color: ${fromTheme('input', 'fontColor', 'disabled')};
    }

    ::placeholder {
        color: ${fromTheme('input', 'placeholderFontColor')};
    }
`;

const InputGl2025Wrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    align-self: stretch;

    border-radius: ${fromTheme('input', 'borderRadius')};
    border-width: ${fromTheme('input', 'borderWidth')};
    border-style: ${fromTheme('input', 'borderStyle')};
    border-color: ${({ destructive }) => destructive ?
        fromTheme('input', 'borderColorDestructive', 'default') :
        fromTheme('input', 'borderColor', 'default')};

    background: ${fromTheme('input', 'backgroundColor', 'default')};

    ${({ pinnedValue, theme }) =>
        pinnedValue &&
        `
            border-left-width: 3px;
            border-left-color: ${fromTheme('input', 'borderColor', 'focus')({ theme })};
        `
    };

    &:hover:not([disabled]):not(:focus-within) {
        border-color: ${({ destructive }) => destructive ?
            fromTheme('input', 'borderColorDestructive', 'hover') :
            fromTheme('input', 'borderColor', 'hover')};
    }

    // style the inner input border when anything within the wrapper is hovered over
    &:hover:not([disabled]):not(:focus-within) input {
        border-color: ${({ destructive }) => destructive ?
            fromTheme('input', 'borderColorDestructive', 'hover') :
            fromTheme('input', 'borderColor', 'hover')};
    }

    &:focus-within {
        border-color: ${({ destructive }) => destructive ?
            fromTheme('input', 'borderColorDestructive', 'focus') :
            fromTheme('input', 'borderColor', 'focus')};
    }

    ${({ disabled, theme }) => disabled && `
        border-color: ${theme.input.borderColor.disabled};
    `}
`;

const LeadingTextDiv = styled.div`
    display: flex;
    align-items: center;
    align-self: stretch;

    padding: ${fromTheme('input', 'leadingText', 'paddingTopBottom')} ${fromTheme('input', 'leadingText', 'paddingLeftRight')};

    color: ${({disabled}) => disabled ?
        fromTheme('input', 'leadingText', 'color', 'disabled') :
        fromTheme('input', 'leadingText', 'color', 'default')};

    ${fromTheme('input', 'leadingText', 'font')};
`;

const TrailingIconDiv = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    background: ${({ disabled }) => disabled ?
        fromTheme('input', 'backgroundColor', 'disabled') :
        fromTheme('input', 'backgroundColor', 'default')};

    color: ${({ disabled, pinnedValue, theme }) => disabled ?
        fromTheme('input', 'trailingIcon', 'color', 'disabled')({ theme }) :
        pinnedValue ?
            fromTheme('input', 'borderColor', 'focus')({ theme }) :
            fromTheme('input', 'trailingIcon', 'color', 'default')({ theme })};

    ${({ pinnedValue, theme }) =>
        pinnedValue
            ? `
                align-self: center;
                padding: 4px;
                margin-right: 4px;
                width: 24px;
                height: 24px;
                border-width: 1px;
                border-style: solid;
                border-color: ${fromTheme('input', 'borderColor', 'focus')({ theme })};
                border-radius: ${fromTheme('input', 'borderRadius')({ theme })};
            `
            : `
                align-self: stretch;
                padding: ${fromTheme('input', 'trailingIcon', 'paddingTopBottom')({ theme })}px ${fromTheme('input', 'trailingIcon', 'paddingLeftRight')({ theme })}px;
                border-width: 0;
                border-top-right-radius: ${fromTheme('input', 'borderRadius')({ theme })};
                border-bottom-right-radius: ${fromTheme('input', 'borderRadius')({ theme })};
            `
    };

    &:hover:not([disabled]) {
        cursor: pointer;
    }
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${fromTheme('input', 'container', 'gap')};
    width: 100%;
`;

const Hint = styled.span`
    ${fromTheme('input', 'hint', 'font')};
    color: ${({ disabled, destructive }) => disabled ?
        fromTheme('input', 'hint', 'color', 'disabled') :
        destructive ?
            fromTheme('input', 'hint', 'color', 'destructive') :
            fromTheme('input', 'hint', 'color', 'default')};
`;

/**
 * downshift dropdowns require a ref
 * @typedef {Object} Props
 * @property {(value: string | number) => void} onChange
 */
/**
 * @type {import('react').ForwardRefRenderFunction<HTMLInputElement, Props>}
 */
const TextInput = forwardRef(function TextInput(props, passedRef) {
    const ref = useEnsureRef(passedRef);
    const {
        multiline = false,
        onBlur: _onBlur,
        onFocus: _onFocus,
        onChange: _onChange,
        autoFocus,
        value,
        id,
        enabled = true,
        name = '',
        sequence,
        type = 'text',
        passRawOnChangeEvent = false,
        disabled = false,
        hint = '',
        destructive = false,
        placeholder = '',
        leadingText = '',
        trailingIcon = '',
        iconOnClick,
        iconAriaAttrs = {},
        maxLength,
        pinnedValue = false
    } = props;

    if (sequence == null && props.type !== 'file') {
        throw new Error(
            `A sequence is required for TextInputs in order to manage focus.  The sequence is missing for id ${id} name ${name}.`
        );
    }

    const focusContext = useContext(FocusContext);
    const [focused, setFocused] = useState(false);
    const [focusProvider, setFocusProvider] = useState();

    const theme = useTheme();
    const themeName = theme.selectedTheme;

    // This updates the FocusProvider with the latest info about this
    // component.
    // It is separate from the registration to avoid the component
    // being deregistered every time a relevant prop changes
    // (e.g. isVisible).
    useEffect(() => {
        if (focusProvider == null) return;
        if (ref.current.focus == null) {
            logging.error('[TextInput] The component does not have a focus method');
        }

        // binding avoids 'illegal invocation' error
        let boundFocus = ref.current.focus;
        boundFocus = boundFocus?.bind(ref.current) ?? boundFocus;
        let focusWrapper = () => {
            if (ref.current == null) return;
            logging.debug(`[TextInput] attempting to focus ${id} | ${name} | sequence ${sequence}`);
            const style = window.getComputedStyle(ref.current);
            if (style.visibility === 'visible') {
                boundFocus();
                if (document.activeElement !== ref.current) {
                    setTimeout(() => {
                        boundFocus();
                    }, 0);
                }
            }
            logging.debug(`[TextInput] name active element: ${document.activeElement?.name}`);
        };

        focusProvider.updateConsumer(focusWrapper, enabled);
    }, [focusProvider, enabled, id, name, sequence, ref]);

    useEffect(() => {
        if (focusContext == null) return;
        if (ref.current.focus == null) {
            logging.error('[TextInput] The component does not have a focus method');
        }
        // register this component with the focusProvider.
        const focusProvider = focusContext.registerConsumer(id, name, sequence);
        setFocusProvider(focusProvider);
        return focusProvider.deregister;
    }, [id, name, sequence, enabled, focusContext, ref]);

    useEffect(() => {
        if (focusContext == null) return;
        if (focused) {
            focusContext.setLastFocused(id);
        }
    }, [focusContext, id, focused]);

    // Keep track of the first value of autoFocus
    const firstAutoFocus = useRef(autoFocus);
    useEffect(() => {
        // If autoFocus changes to true, but wasn't true at first, then we'll need
        // to force the focus here.  This might happen if the autoFocused input changes
        // to this one because of some async operation (like maybe another field
        // had a default value that had to be looked up asynchronously and was
        // no longer the default because it wasn't empty after the lookup).
        if (autoFocus && !firstAutoFocus.current) {
            ref.current?.focus();
        }
    }, [autoFocus, ref]);

    // React will only maintain cursor position within the input if the changes are
    // synchronous.  Some of our default lookups, etc. are async, so this will keep a version
    // of the value that is local to the component and synchronous to avoid losing
    // the cursor position.
    const [localValue, setLocalValue] = useState(value);

    const onFocus = useCallback(
        e => {
            logging.debug(`[TextInput] onFocus called for ${id} | ${name}.`);
            setFocused(true);
            if (_onFocus != null) {
                _onFocus(e);
            } else {
                e.currentTarget.select();
            }
        },
        [_onFocus, id, name]
    );

    const onBlur = useCallback(
        e => {
            logging.debug(`[TextInput] onBlur called ${id} | ${name}.`);
            setFocused(false);
            _onBlur?.(e);
        },
        [_onBlur, id, name]
    );

    const onChange = useCallback(
        e => {
            if (maxLength) {
                setLocalValue(e.target.value.substring(0, maxLength));
            } else {
                setLocalValue(e.target.value);
            }

            if (passRawOnChangeEvent) {
                _onChange?.(e);
                return;
            }
            _onChange?.(maxLength ? e.target.value.substring(0, maxLength) : e.target.value);
        },
        [_onChange, maxLength, passRawOnChangeEvent]
    );

    useEffect(() => {
        // don't allow 'e' to be typed with numbers (by default type = 'number' allows 'e' to be typed for scientific notation)
        const onKeyDown = evt => {
            if (type === 'number' && evt.key === 'e') {
                evt.preventDefault();
            }
        };

        const element = ref.current;

        element.addEventListener('keydown', onKeyDown);
        return () => {
            element.removeEventListener('keydown', onKeyDown);
        };
    });

    useEffect(() => {
        setLocalValue(prev => {
            if (!isEqual(prev, value)) {
                return value;
            }
            return prev;
        });
        focusProvider?.updateConsumer(undefined, undefined, value);
    }, [value, focusProvider]);

    if (multiline) {
        return rc(Container, {},
            rc(themeName === 'gl2025' ? TextAreaGl2025 : TextAreaOld, {
                ...props,
                id,
                enabled,
                value: localValue,
                onBlur,
                onFocus,
                spellCheck: 'false',
                ref,
                onChange
            }),
            themeName === 'gl2025' && hint && hint.length && rc(Hint, { disabled, destructive }, hint)
        );
    }

    const itemLeading = leadingText && leadingText.length;
    const itemTrailing = trailingIcon && trailingIcon.length;
    return rc(Container, {},
        themeName === 'gl2025' && rc(InputGl2025Wrapper, { disabled, destructive, pinnedValue },
            itemLeading && rc(LeadingTextDiv, { disabled }, leadingText),
            rc(InputGl2025, {
                ...props,
                id,
                enabled,
                value: localValue,
                onBlur,
                onFocus,
                spellCheck: 'false',
                ref,
                'aria-label': props.ariaLabel,
                onChange,
                type,
                placeholder,
                itemLeading,
                itemTrailing,
                pinnedValue
            }),
            itemTrailing && rc(TrailingIconDiv, {
                    as: iconOnClick ? 'button' : undefined,
                    type: iconOnClick ? 'button' : undefined,
                    disabled,
                    pinnedValue,
                    onClick: disabled ? null : iconOnClick,
                    'data-testid': `${id}-trailing-icon`,
                    ...(!iconOnClick ? { 'aria-hidden': true, tabIndex: -1 } : null),
                    ...iconAriaAttrs
                },
                rc(BaseIcon, { name: trailingIcon, color: 'currentColor', size: 'sm', fill: pinnedValue })
            )
        ),
        themeName !== 'gl2025' && rc(InputOld, {
            ...props,
            id,
            enabled,
            value: localValue,
            onBlur,
            onFocus,
            spellCheck: 'false',
            ref,
            'aria-label': props.ariaLabel,
            onChange,
            type
        }),
        themeName === 'gl2025' && hint && hint.length && rc(Hint, { disabled, destructive }, hint)
    );
});
TextInput.propTypes = {
    sequence: propTypes.number,
    type: propTypes.string,
    ariaLabel: propTypes.string
};
export default TextInput;
